import type { useForm } from 'react-hook-form';
import { isApiError } from '@shigo-ui/api-client';

import { dashboardT, type DashboardMessageKey } from '../../../../../lib/messages';
import { isAssignableShigoRole, ShigoRole } from '../../../../auth/permissions';
import type { AdminUser, AdminUserNetwork } from '../../model';
import type { AdminUserFormValue } from './UserDialog.schema';

export function translateValidationMessage(message: string) {
  return dashboardT(message as DashboardMessageKey);
}

export const emptyAllowedRolesQuery = { data: [], isFetching: false, isError: true };

export function membershipsForUser(user: AdminUser | null): AdminUserFormValue['memberships'] {
  if (!user) return [];
  if (user.memberships.length > 0) {
    return user.memberships.map((membership) => ({
      networkId: String(membership.networkId),
      agencyIds: [...(membership.agencyIds ?? [])],
      roles: membership.roles.filter(isAssignableShigoRole),
      publisherIds: membership.roles.includes(ShigoRole.Publisher)
        ? [...membership.publisherIds]
        : [],
    }));
  }
  if (!user.network) return [];
  return [
    {
      networkId: String(user.network.id),
      agencyIds: [...(user.agencyIds ?? [])],
      roles: user.roles.filter(isAssignableShigoRole),
      publisherIds: user.roles.includes(ShigoRole.Publisher) ? [...user.publisherIds] : [],
    },
  ];
}

export function networksForUser(networks: AdminUserNetwork[], user: AdminUser | null) {
  const result = new Map(networks.map((network) => [String(network.id), network]));
  user?.memberships.forEach((membership) => {
    const networkId = String(membership.networkId);
    if (!result.has(networkId)) {
      result.set(networkId, { id: membership.networkId, name: membership.networkName });
    }
  });
  if (user?.network && !result.has(String(user.network.id))) {
    result.set(String(user.network.id), user.network);
  }
  return [...result.values()];
}

export function applyApiFieldErrors(
  error: unknown,
  setError: ReturnType<typeof useForm<AdminUserFormValue>>['setError'],
) {
  if (!isApiError(error) || !error.fields) return;
  const supportedFields = [
    'email',
    'name',
    'password',
    'roles',
    'publisherIds',
    'agencyIds',
    'networkId',
  ] as const;
  supportedFields.forEach((field) => {
    if (error.fields?.[field]) {
      setError(field, { message: 'users.validation.invalid' });
    }
  });
}

export function clearAgencySelection(
  form: ReturnType<typeof useForm<AdminUserFormValue>>,
  roles: string[],
  name: 'agencyIds' | `memberships.${number}.agencyIds` = 'agencyIds',
) {
  if (!roles.includes(ShigoRole.Agency)) {
    form.setValue(name, [], { shouldDirty: true, shouldValidate: true });
  }
}
